import { z } from 'zod';

import { AgentsLifecycleSchema } from './agents';
import { ConversationIdSchema } from './message_envelope';
import { EnvelopeOrgIdSchema } from './org_id';

/** Telemetria de um turno do agente (agents → analytics), publicada no mesmo tópico das respostas
 *  com o atributo SNS `kind=agent_turn` e SEM `status`: a assinatura do conversation filtra por
 *  `status` e nunca recebe isto. Nenhum texto do cliente ou do agente. */
export const AGENT_TURN_KIND = 'agent_turn';
export const AGENT_TURN_EVENT_TYPE = 'agent.turn.completed';

export const AgentTurnCallSchema = z
  .object({
    tool: z.string().min(1).max(120),
    service: z.string().min(1).max(60),
    ok: z.boolean(),
    httpStatus: z.number().int().optional(),
    ms: z.number().min(0),
  })
  .strict();
export type AgentTurnCall = z.infer<typeof AgentTurnCallSchema>;

export const AgentTurnLlmUsageSchema = z
  .object({
    agent: z.string().max(60),
    /** Alias que a chamada pediu (`fast`, `default`…), como o proxy devolve no corpo. */
    model: z.string().max(120),
    /** Modelo do deployment por trás do alias (header `x-litellm-model-name`); null sem o header. */
    deployedModel: z.string().max(120).nullable().optional(),
    calls: z.number().int().min(0),
    inputTokens: z.number().int().min(0),
    outputTokens: z.number().int().min(0),
    costUsd: z.number().min(0).nullable(),
  })
  .strict();
export type AgentTurnLlmUsage = z.infer<typeof AgentTurnLlmUsageSchema>;

const AgentTurnLlmSchema = z
  .object({
    calls: z.number().int().min(0),
    inputTokens: z.number().int().min(0),
    outputTokens: z.number().int().min(0),
    costUsd: z.number().min(0).nullable().optional(),
    usage: z.array(AgentTurnLlmUsageSchema).max(50).optional(),
  })
  .strict();

export const AgentTurnCompletedSchema = z
  .object({
    type: z.literal(AGENT_TURN_EVENT_TYPE),
    version: z.literal(1),
    orgId: EnvelopeOrgIdSchema,
    conversationId: ConversationIdSchema,
    correlationId: z.uuid(),
    traceId: z
      .string()
      .regex(/^[0-9a-f]{32}$/)
      .nullable(),
    at: z.iso.datetime(),
    durationMs: z.number().int().min(0),
    target: z.string().max(60).nullable(),
    routingSource: z.string().max(40).nullable(),
    rerouteCount: z.number().int().min(0),
    lifecycle: AgentsLifecycleSchema,
    uiKinds: z.array(z.string().max(60)).max(50),
    turnError: z
      .string()
      .regex(/^[a-z][a-z0-9_.]{0,60} failed$/)
      .nullable(),
    calls: z.array(AgentTurnCallSchema).max(200),
    errorCount: z.number().int().min(0),
    llm: AgentTurnLlmSchema,
  })
  .strict();
export type AgentTurnCompleted = z.infer<typeof AgentTurnCompletedSchema>;

/** O mesmo contrato para quem CONSOME: campos desconhecidos (de um produtor já com versão nova,
 *  durante o deploy) são descartados em vez de recusar a mensagem inteira. O produtor segue com o
 *  estrito acima. */
export const AgentTurnCompletedWireSchema = z.object({
  ...AgentTurnCompletedSchema.shape,
  calls: z.array(z.object(AgentTurnCallSchema.shape)).max(200),
  llm: z.object({
    ...AgentTurnLlmSchema.shape,
    usage: z.array(z.object(AgentTurnLlmUsageSchema.shape)).max(50).optional(),
  }),
});
